import React, { useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const AboutUs = () => {
    const navigate = useNavigate();

    const {
        store: { selectedStore },
    }: any = useSelector((state: any) => state);

    const storeImgUrl = useMemo(() => {
        if (selectedStore?.banner_image) {
            return (
                selectedStore.banner_image.base_url +
                selectedStore.banner_image.attachment_url
            );
        }
        return "/images/bg.jpg";
    }, [selectedStore]);

    // Hide scrollbar only on this page
    useEffect(() => {
        const root = document.getElementById("root");

        if (root) {
            root.classList.add("about-scroll-hide");
        }

        return () => {
            if (root) {
                root.classList.remove("about-scroll-hide");
            }
        };
    }, []);

    return (
        <div className="bg-white min-h-screen w-full overflow-x-hidden">

            {/* HERO */}
            <section className="relative w-full h-[260px] md:h-[380px] lg:h-[480px] overflow-hidden">
                <img
                    src={storeImgUrl}
                    alt="Restaurant Banner"
                    className="w-full h-full object-cover"
                    loading="lazy"
                />

                <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-center px-4">
                    <h1 className="text-white text-3xl md:text-5xl font-semibold mb-4">
                        {selectedStore?.name || "About Our Restaurant"}
                    </h1>

                    <p className="text-gray-200 max-w-2xl text-sm md:text-base mb-6">
                        Experience delicious food, warm hospitality and unforgettable moments.
                    </p>

                    <button
                        onClick={() => navigate("/menu")}
                        className="bgBlue text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition shadow-md"
                    >
                        Order Now
                    </button>
                </div>
            </section>

            {/* ABOUT CONTENT */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-20">
                <div className="grid md:grid-cols-2 gap-14 items-center">

                    {/* IMAGE */}
                    <div className="rounded-xl overflow-hidden shadow-lg">
                        <img
                            src="/images/about-food.jpg"
                            alt="Food"
                            className="w-full h-[320px] md:h-[420px] object-cover"
                            loading="lazy"
                        />
                    </div>

                    {/* TEXT */}
                    <div>
                        <h2 className="text-3xl font-semibold textLightBlack mb-6">
                            Our Story
                        </h2>

                        <p className="textLightBlack leading-7 mb-5">
                            {selectedStore?.description ||
                                "We started with a passion for serving fresh, high-quality meals that bring people together. Every dish is crafted with care using the finest ingredients."}
                        </p>

                        <p className="textLightBlack leading-7 mb-8">
                            Our chefs combine traditional flavors with modern creativity to create an unforgettable dining experience for every guest.
                        </p>

                        {/* STATS */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                            <div className="bg-gray-50 p-5 rounded-lg shadow-sm">
                                <h3 className="textBlue text-2xl font-semibold">10+</h3>
                                <p className="text-sm textLightBlack">Years Experience</p>
                            </div>
                            <div className="bg-gray-50 p-5 rounded-lg shadow-sm">
                                <h3 className="textBlue text-2xl font-semibold">50K+</h3>
                                <p className="text-sm textLightBlack">Happy Customers</p>
                            </div>
                            <div className="bg-gray-50 p-5 rounded-lg shadow-sm">
                                <h3 className="textBlue text-2xl font-semibold">100+</h3>
                                <p className="text-sm textLightBlack">Dishes Served</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MISSION + CTA */}
            <section className="bg-gray-50 py-16">
                <div className="max-w-4xl mx-auto text-center px-6">
                    <h2 className="text-3xl font-semibold textLightBlack mb-6">
                        Our Mission
                    </h2>

                    <p className="textLightBlack leading-7 mb-8">
                        Our mission is to provide exceptional food and service in a warm,
                        welcoming atmosphere. We believe dining is more than just eating —
                        it’s about creating memories with the people you love.
                    </p>

                    <button
                        onClick={() => navigate("/menu")}
                        className="border borderBlue textBlue px-6 py-3 rounded-lg font-medium hover:bgBlue transition"
                    >
                        Explore Our Menu
                    </button>
                </div>
            </section>

        </div>
    );
};

export default AboutUs;